/**
 * ExteriorStudioPage — SANDBOX. Studio Extérieur piloté par la carte 3D.
 * Modules : A (recherche + zoom cinématique + zone figée + emprise + GLB),
 * B (panneau génération extérieure), C (Mes Adresses + orbite 360°).
 * Réutilise Hub3DMap (tuiles 3D Google + MapVRAMGuard) et AddressGeocoderSearch.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Unlock, PenTool, Check, Trash2, Box } from 'lucide-react';
import Hub3DMap from '../../components/hub/Hub3DMap';
import AddressGeocoderSearch from '../../components/hub/AddressGeocoderSearch';
import { MyAddressesSidebar } from './MyAddressesSidebar';
import { ExteriorPanel } from './ExteriorPanel';
import { GlbOverlay } from './GlbOverlay';
import { RenderOverlay } from './RenderOverlay';
import { BeforeAfterView } from './BeforeAfterView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const opts = { withCredentials: true };

const ToolBtn = ({ onClick, active, disabled, icon: Icon, label, testid, tone = 'gold' }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[11.5px] font-semibold transition-colors disabled:opacity-40"
    style={{
      background: active ? (tone === 'red' ? 'rgba(244,63,94,0.22)' : 'rgba(217,160,91,0.22)') : 'rgba(10,10,13,0.82)',
      color: active ? (tone === 'red' ? '#FDA4AF' : 'var(--c-gold)') : '#D6D6DE',
      border: `1px solid ${active ? (tone === 'red' ? 'rgba(244,63,94,0.5)' : 'var(--c-border-accent)') : 'rgba(255,255,255,0.12)'}`,
      backdropFilter: 'blur(8px)',
    }}>
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

export default function ExteriorStudioPage() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);       // map_project actif
  const [flyTo, setFlyTo] = useState(null);
  const [orbitId, setOrbitId] = useState(null);     // déclenche FlyToSelected (orbite)
  const [drawMode, setDrawMode] = useState(false);
  const [draftFootprint, setDraftFootprint] = useState([]);
  const [frozen, setFrozen] = useState(null);       // {lat,lng,range,tilt,heading}
  const [panelOpen, setPanelOpen] = useState(false);
  const [overlayRender, setOverlayRender] = useState(null); // render superposé
  const [beforeAfter, setBeforeAfter] = useState(null);     // render en mode Avant/Après

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/map-projects`, opts);
      setItems(r.data.items || []);
    } catch { /* silencieux */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshActive = useCallback(async (id) => {
    const r = await axios.get(`${API}/map-projects/${id}`, opts);
    setActive(r.data);
    setItems((prev) => prev.map((it) => it.map_project_id === id ? r.data : it));
    return r.data;
  }, []);

  // A1/A2 — recherche adresse → zoom cinématique + création de l'adresse
  const handleAddress = useCallback(async (res) => {
    setFlyTo({ lat: res.lat, lon: res.lon, range: 700, tilt: 60, nonce: Date.now() });
    setFrozen(null); setOrbitId(null); setDraftFootprint([]);
    try {
      const r = await axios.post(`${API}/map-projects`, {
        name: (res.display_name || 'Adresse').split(',')[0],
        address: res.display_name, lat: res.lat, lng: res.lon,
      }, opts);
      setActive(r.data);
      load();
      toast.success('Adresse ajoutée à « Mes Adresses »');
    } catch { toast.error('Création impossible'); }
  }, [load]);

  // A4 — dessin d'emprise : clics carte → sommets géo
  const handleMapClick = useCallback((lat, lng) => {
    if (!drawMode) return;
    setDraftFootprint((prev) => [...prev, { lat, lng }]);
  }, [drawMode]);

  const commitFootprint = useCallback(async () => {
    if (!active || draftFootprint.length < 3) { toast.error('Placez au moins 3 sommets'); return; }
    await axios.patch(`${API}/map-projects/${active.map_project_id}`, { footprint: draftFootprint }, opts);
    await refreshActive(active.map_project_id);
    setDrawMode(false); setDraftFootprint([]);
    toast.success('Emprise enregistrée');
  }, [active, draftFootprint, refreshActive]);

  // A3 — figer la zone : caméra verrouillée + persistée
  const freeze = useCallback(async (proj = active) => {
    if (!proj) return;
    const fp = proj.footprint?.length ? proj.footprint : draftFootprint;
    const center = fp.length
      ? { lat: fp.reduce((s, p) => s + p.lat, 0) / fp.length, lng: fp.reduce((s, p) => s + p.lng, 0) / fp.length }
      : { lat: proj.coords[0], lng: proj.coords[1] };
    const cam = { ...center, range: 320, tilt: 62, heading: 0 };
    setFlyTo({ lat: cam.lat, lon: cam.lng, range: cam.range, tilt: cam.tilt, heading: cam.heading, nonce: Date.now() });
    setFrozen(cam);
    setDrawMode(false);
    try {
      await axios.patch(`${API}/map-projects/${proj.map_project_id}`, { frozen_camera: cam }, opts);
    } catch { /* non bloquant */ }
  }, [active, draftFootprint]);

  // C — clic sur une adresse : zoom auto + orbite 360° + mode figé
  const openProject = useCallback(async (mp) => {
    const doc = await refreshActive(mp.map_project_id);
    setOverlayRender(null); setDraftFootprint([]); setFrozen(null);
    setOrbitId(mp.map_project_id);          // FlyToSelected → plongée + flyCameraAround
    setTimeout(() => { setOrbitId(null); freeze(doc); setPanelOpen(true); }, 10500);
  }, [refreshActive, freeze]);

  // Emprises affichées (projet actif en surbrillance + brouillon)
  const footprints = useMemo(() => {
    const out = [];
    for (const it of items) {
      if (it.footprint?.length >= 3) {
        out.push({
          project_id: it.map_project_id,
          polygon_latlon: it.footprint.map((p) => [p.lat, p.lng]),
          selected: active?.map_project_id === it.map_project_id,
        });
      }
    }
    if (draftFootprint.length >= 2) {
      out.push({ project_id: '_draft', polygon_latlon: draftFootprint.map((p) => [p.lat, p.lng]), selected: true });
    }
    return out;
  }, [items, draftFootprint, active]);

  const orbitProjects = useMemo(() => items.map((it) => ({
    project_id: it.map_project_id, coords: it.coords, name: it.name, published: true,
  })), [items]);

  return (
    <div className="relative w-full h-screen overflow-hidden" style={{ background: '#0A0A0D' }}
         data-testid="exterior-studio-page">
      <Hub3DMap
        projects={orbitProjects}
        selectedId={orbitId}
        onMarkerClick={(p) => { const it = items.find((i) => i.map_project_id === p.project_id); if (it) openProject(it); }}
        defaultCenter={{ lat: 46.6, lng: 2.45 }}
        apiKey={MAPS_KEY}
        flyTo={flyTo}
        onMapClick={handleMapClick}
        planFootprints={footprints}
        onFootprintClick={(fp) => {
          const it = items.find((i) => i.map_project_id === fp.project_id);
          if (it) { refreshActive(it.map_project_id); setPanelOpen(true); }
        }}
      />

      {/* A1 — recherche épurée en surimpression */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] w-[440px] max-w-[90vw]"
           data-testid="studio-search-wrap">
        <AddressGeocoderSearch onResult={handleAddress}
          placeholder="Rechercher une adresse pour démarrer…" testid="studio-address-search" />
      </div>

      {/* Barre d'outils zone */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[500] flex items-center gap-2"
           data-testid="studio-toolbar">
        <ToolBtn icon={PenTool} label={drawMode ? `Emprise · ${draftFootprint.length} pts` : 'Définir l\u2019emprise'}
          active={drawMode} disabled={!active || !!frozen}
          onClick={() => { setDrawMode((v) => !v); if (drawMode) setDraftFootprint([]); }}
          testid="studio-draw-btn" />
        {drawMode && (
          <>
            <ToolBtn icon={Check} label="Terminer" onClick={commitFootprint} testid="studio-draw-commit" />
            <ToolBtn icon={Trash2} label="Effacer" onClick={() => setDraftFootprint([])} testid="studio-draw-clear" />
          </>
        )}
        {!frozen ? (
          <ToolBtn icon={Lock} label="Figer la zone" disabled={!active} onClick={() => freeze()} testid="studio-freeze-btn" />
        ) : (
          <ToolBtn icon={Unlock} label="Défiger" active tone="red" onClick={() => setFrozen(null)} testid="studio-unfreeze-btn" />
        )}
        <ToolBtn icon={Box} label="Éditer / Générer" disabled={!active}
          onClick={() => setPanelOpen(true)} testid="studio-open-panel-btn" />
      </div>

      {/* A3 — cadre « zone figée » : bloque pan/zoom, clic = ouvrir l'édition */}
      {frozen && (
        <div className="absolute inset-0 z-[450] cursor-pointer" data-testid="studio-frozen-overlay"
             onClick={() => setPanelOpen(true)}
             style={{ boxShadow: 'inset 0 0 0 3px rgba(217,160,91,0.75), inset 0 0 80px rgba(0,0,0,0.35)' }}>
          <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-md text-[10px] font-bold tracking-[0.2em] uppercase"
               style={{ background: 'rgba(10,10,13,0.85)', color: 'var(--c-gold)', border: '1px solid var(--c-border-accent)' }}>
            <Lock className="w-3 h-3 inline mr-1.5 -mt-0.5" />Zone figée — cliquez pour éditer
          </div>
        </div>
      )}

      {/* A5 — modèle GLB superposé (mise à l'échelle directe) */}
      {frozen && active?.glb_path && (
        <GlbOverlay project={active} onConfigSaved={() => refreshActive(active.map_project_id)} />
      )}

      {/* B4 — rendu généré superposé sur la zone figée */}
      {frozen && overlayRender && (
        <RenderOverlay src={`${API}/map-projects/${active.map_project_id}/renders/${overlayRender}/image`}
          onClose={() => setOverlayRender(null)} />
      )}

      {/* Mode Avant / Après — satellite réel vs projet IA */}
      {beforeAfter && active && (
        <BeforeAfterView
          beforeSrc={`${API}/map-projects/${active.map_project_id}/renders/${beforeAfter}/before?maps_key=${MAPS_KEY}`}
          afterSrc={`${API}/map-projects/${active.map_project_id}/renders/${beforeAfter}/image`}
          onClose={() => setBeforeAfter(null)}
        />
      )}

      {/* C — Mes Adresses */}
      <MyAddressesSidebar items={items} activeId={active?.map_project_id}
        onSelect={openProject} onDeleted={load} />

      {/* B — panneau de génération extérieure */}
      {panelOpen && active && (
        <ExteriorPanel project={active} mapsKey={MAPS_KEY}
          onClose={() => setPanelOpen(false)}
          onRefresh={() => refreshActive(active.map_project_id)}
          onOverlay={(rid) => { if (!frozen) freeze(); setOverlayRender(rid); }}
          onBeforeAfter={(rid) => { setPanelOpen(false); setBeforeAfter(rid); }} />
      )}
    </div>
  );
}
